import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  APPLICANT_STATUSES, APPLICANT_STATUS_COLORS, APPLICANT_SOURCES,
  formatEnumLabel,
} from '../../../../constants/hrm';
import {
  useApplicants, useCreateApplicant,
  useJobs,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_id: '',
  source: 'CAREER_PAGE',
  resume_url: '',
  current_company: '',
  experience_years: '',
  expected_salary: '',
};

const ApplicantList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const filters = {
    page: page + 1,
    per_page: rowsPerPage,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(jobFilter && { job_id: jobFilter }),
  };

  const { data: applicantsData, isLoading: loading, refetch: refetchApplicants } = useApplicants(chatbotId, filters, { enabled: !!chatbotId && canView });
  const { data: jobsData } = useJobs(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });

  const applicants = applicantsData?.data || [];
  const totalCount = applicantsData?.pagination?.total || 0;
  const jobs = jobsData?.data || [];

  const createApplicant = useCreateApplicant(chatbotId);
  const saving = createApplicant.isPending;

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleOpenDialog = () => {
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.job_id) {
      setError('Job is required');
      return;
    }
    try {
      const payload = {
        ...formData,
        job_id: Number(formData.job_id),
        experience_years: formData.experience_years ? Number(formData.experience_years) : null,
        expected_salary: formData.expected_salary ? Number(formData.expected_salary) : null,
      };
      await createApplicant.mutateAsync(payload);
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create applicant');
    }
  };

  const handleRowClick = (applicant) => {
    navigate(`/chatbot/${chatbotId}/hrm/applicants/${applicant.id}`);
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Applicants
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetchApplicants()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenDialog}>
              Add Applicant
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search applicants..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {APPLICANT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Job</InputLabel>
          <Select
            value={jobFilter}
            label="Job"
            onChange={(e) => { setJobFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All Jobs</MenuItem>
            {jobs.map((j) => (
              <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Job Title</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell>Applied Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applicants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No applicants found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  applicants.map((applicant) => (
                    <TableRow
                      key={applicant.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(applicant)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {applicant.first_name} {applicant.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{applicant.email}</TableCell>
                      <TableCell>{applicant.job_title || '-'}</TableCell>
                      <TableCell>{applicant.source || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={applicant.status}
                          size="small"
                          sx={{
                            backgroundColor: APPLICANT_STATUS_COLORS[applicant.status] || '#9e9e9e',
                            color: '#fff',
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {applicant.score != null ? applicant.score : '-'}
                      </TableCell>
                      <TableCell>
                        {applicant.applied_date
                          ? new Date(applicant.applied_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(applicant);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Applicant</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Job *</InputLabel>
                <Select
                  value={formData.job_id}
                  label="Job *"
                  onChange={(e) => handleFormChange('job_id', e.target.value)}
                >
                  {jobs.map((j) => (
                    <MenuItem key={j.id} value={j.id}>{j.title} ({j.status})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  label="Source"
                  onChange={(e) => handleFormChange('source', e.target.value)}
                >
                  {APPLICANT_SOURCES.map((s) => (
                    <MenuItem key={s} value={s}>{formatEnumLabel(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Resume URL"
                value={formData.resume_url}
                onChange={(e) => handleFormChange('resume_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Company"
                value={formData.current_company}
                onChange={(e) => handleFormChange('current_company', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={formData.experience_years}
                onChange={(e) => handleFormChange('experience_years', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expected Salary"
                type="number"
                value={formData.expected_salary}
                onChange={(e) => handleFormChange('expected_salary', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.first_name || !formData.last_name || !formData.email}
          >
            {saving ? <CircularProgress size={24} /> : 'Add Applicant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicantList;
